import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import confirmUnsavedChangesSwal from "../utils/confirmUnsavedChangesSwal";

/**
 * Staff-style unsaved-changes guard:
 * - Themed SweetAlert: Cancel · Don't Save · Save
 * - useBlocker for in-app navigation (requires data router)
 * - beforeunload for refresh / close tab
 *
 * @param {object} options
 * @param {boolean} options.isDirty
 * @param {() => Promise<boolean>} options.onSave - persist without exiting; return true on success
 * @param {() => void} [options.onDiscard] - restore / clear dirty state before leaving
 * @param {boolean} [options.enabled=true]
 */
export default function useUnsavedChangesGuard({
  isDirty,
  onSave,
  onDiscard,
  enabled = true,
}) {
  const promptOpenRef = useRef(false);
  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  const blockerRef = useRef(null);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    onDiscardRef.current = onDiscard;
  }, [onDiscard]);

  const shouldBlock = Boolean(enabled && isDirty);

  const blocker = useBlocker(shouldBlock);
  blockerRef.current = blocker;

  const confirmLeave = useCallback(async () => {
    if (!shouldBlock) return true;
    if (promptOpenRef.current) return false;

    promptOpenRef.current = true;
    try {
      const result = await confirmUnsavedChangesSwal();

      if (result === "discard") {
        if (typeof onDiscardRef.current === "function") {
          onDiscardRef.current();
        }
        return true;
      }
      if (result === "save") {
        const success = await onSaveRef.current();
        return Boolean(success);
      }
      return false;
    } finally {
      promptOpenRef.current = false;
    }
  }, [shouldBlock]);

  // Only react when blocker enters "blocked" — avoid re-opening on every render
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (promptOpenRef.current) return;

    let cancelled = false;

    (async () => {
      const allow = await confirmLeave();
      if (cancelled) return;
      const current = blockerRef.current;
      if (!current || current.state !== "blocked") return;
      if (allow) {
        current.proceed();
      } else {
        current.reset();
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally depend on blocker.state only (not full blocker object)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state, confirmLeave]);

  useEffect(() => {
    if (!shouldBlock) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);

  return { confirmLeave, blocker };
}
